import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Building2, Sparkles, TriangleAlert, User } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import { Segmented } from '@/pages/catalog/shared';
import {
  CreateViewingInput, MatchUnitsResponse, PropertyUnit, UpdateViewingInput, Viewing, realEstateService,
} from '@/services/real-estate.service';
import {
  ContactPicker, LeadPicker, PickedChip, PickedContact, PickedLead, UnitPicker, apiMessage, browserTimezone, isoToZoned,
  personName, placeLine, unitSpecs, useCompanyTimezone, useRealEstateAccess, useTeam, zonedToIso,
} from './shared';

type Who = 'lead' | 'contact' | 'other';
const DURATIONS = [15, 30, 45, 60, 90, 120];
/** The backend refuses viewings on these. */
const CLOSED = ['sold', 'off_market'];
const viewable = (u: PropertyUnit) => !CLOSED.includes(u.status);

/** Next half hour (at least 5 minutes out) on the company clock. */
function nextSlot(tz: string): { ymd: string; hm: string } {
  const ms = Math.ceil((Date.now() + 5 * 60_000) / 1_800_000) * 1_800_000;
  return isoToZoned(new Date(ms).toISOString(), tz);
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (v: Viewing) => void;
  /** Reschedule this viewing (agent, time, length, notes) instead of booking a new one. */
  viewing?: Viewing | null;
  /** Pre-picked unit / lead for a new viewing — pass values held in state, not fresh objects. */
  unit?: PropertyUnit | null;
  lead?: PickedLead | null;
}

export default function ScheduleViewingModal({ open, onClose, onSaved, viewing, unit: presetUnit, lead: presetLead }: Props) {
  const t = useLabels();
  const { user } = useAuth();
  const tz = useCompanyTimezone();
  const companyCurrency = useCompanyCurrency();
  const { leadSearch, contactSearch } = useRealEstateAccess();
  const team = useTeam();
  const editing = !!viewing;

  const [unit, setUnit] = useState<PropertyUnit | null>(null);
  const [who, setWho] = useState<Who>('lead');
  const [lead, setLead] = useState<PickedLead | null>(null);
  const [contact, setContact] = useState<PickedContact | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [agent, setAgent] = useState('');
  const [agentTouched, setAgentTouched] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState('');
  const [conflict, setConflict] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState<MatchUnitsResponse['items']>([]);

  const whoOptions = useMemo(() => {
    const opts: Array<{ value: Who; label: string }> = [];
    if (leadSearch) opts.push({ value: 'lead', label: t('leads', 'singular') });
    if (contactSearch) opts.push({ value: 'contact', label: t('contacts', 'singular') });
    opts.push({ value: 'other', label: 'Someone else' });
    return opts;
  }, [leadSearch, contactSearch, t]);

  useEffect(() => {
    if (!open) return;
    setConflict(null);
    setMatches([]);
    if (viewing) {
      const z = isoToZoned(viewing.scheduledAt, tz);
      setDate(z.ymd);
      setTime(z.hm);
      setDuration(viewing.durationMinutes || 30);
      setAgent(viewing.agentUserUid ?? '');
      setAgentTouched(true);
      setNotes(viewing.notes ?? '');
      return;
    }
    const slot = nextSlot(tz);
    setUnit(presetUnit ?? null);
    setLead(presetLead ?? null);
    setContact(null);
    setName('');
    setPhone('');
    setWho(presetLead || leadSearch ? 'lead' : contactSearch ? 'contact' : 'other');
    setAgent('');
    setAgentTouched(false);
    setDate(slot.ymd);
    setTime(slot.hm);
    setDuration(30);
    setNotes('');
  }, [open, viewing, presetUnit, presetLead, tz, leadSearch, contactSearch]);

  // New viewings default to the person booking them, once the team list is in.
  useEffect(() => {
    if (!open || editing || agentTouched || agent) return;
    if (user?.userUid && team.some((u) => u.userUid === user.userUid)) setAgent(user.userUid);
  }, [open, editing, agentTouched, agent, team, user?.userUid]);

  // A picked enquiry with no unit yet: offer the units that fit what they asked for.
  useEffect(() => {
    if (!open || editing || unit || who !== 'lead' || !lead) { setMatches([]); return; }
    let live = true;
    realEstateService.matchUnits({ leadUid: lead.leadUid })
      .then((r) => { if (live) setMatches(r.hasCriteria ? r.items.filter(viewable).slice(0, 3) : []); })
      .catch(() => { if (live) setMatches([]); });
    return () => { live = false; };
  }, [open, editing, unit, who, lead]);

  const scheduledIso = date && time ? zonedToIso(date, time, tz) : null;
  const inPast = scheduledIso ? new Date(scheduledIso).getTime() < Date.now() - 60_000 : false;
  const durations = DURATIONS.includes(duration) ? DURATIONS : [...DURATIONS, duration].sort((a, b) => a - b);
  const tzLabel = tz.replace(/_/g, ' ');

  const submit = async () => {
    setConflict(null);
    if (!scheduledIso) { toast.error('Pick a date and time'); return; }
    let body: CreateViewingInput | null = null;
    if (!viewing) {
      if (!unit) { toast.error('Pick the unit to view'); return; }
      if (who === 'lead' && !lead) { toast.error(`Pick the ${t('leads', 'singular').toLowerCase()}`); return; }
      if (who === 'contact' && !contact) { toast.error(`Pick the ${t('contacts', 'singular').toLowerCase()}`); return; }
      if (who === 'other' && !name.trim() && !phone.trim()) { toast.error('Enter the customer’s name or phone'); return; }
      body = {
        unitUid: unit.unitUid,
        leadUid: who === 'lead' ? lead?.leadUid : undefined,
        contactUid: who === 'contact' ? contact?.contactUid : undefined,
        customerName: who === 'other' ? name.trim() || undefined : undefined,
        customerPhone: who === 'other' ? phone.trim() || undefined : undefined,
        agentUserUid: agent || undefined,
        scheduledAt: scheduledIso,
        durationMinutes: duration,
        notes: notes.trim() || undefined,
      };
    }
    setBusy(true);
    try {
      let saved: Viewing;
      if (viewing) {
        const patch: UpdateViewingInput = {};
        if (agent !== (viewing.agentUserUid ?? '')) patch.agentUserUid = agent;
        // The form works in whole minutes; a stored time with seconds is not a change.
        const minuteOf = (iso: string) => Math.floor(new Date(iso).getTime() / 60_000);
        if (minuteOf(scheduledIso) !== minuteOf(viewing.scheduledAt)) patch.scheduledAt = scheduledIso;
        if (duration !== viewing.durationMinutes) patch.durationMinutes = duration;
        if (notes.trim() !== (viewing.notes ?? '').trim()) patch.notes = notes.trim();
        if (Object.keys(patch).length === 0) { onClose(); return; }
        // 409 = the agent is double-booked; its message is shown in the form instead of a toast.
        saved = await realEstateService.updateViewing(viewing.viewingUid, patch, { expectedStatuses: [409] });
        toast.success('Viewing updated');
      } else if (body) {
        saved = await realEstateService.createViewing(body, { expectedStatuses: [409] });
        toast.success('Viewing scheduled');
      } else {
        return;
      }
      onSaved(saved);
    } catch (e: unknown) {
      const res = (e as { response?: { status?: number } })?.response;
      if (res?.status === 409) setConflict(apiMessage(e) ?? 'This agent already has a viewing at that time.');
      else if (!res) toast.error('Could not save — check your connection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={editing ? 'Reschedule viewing' : 'Schedule a viewing'} widthClass="max-w-xl">
      {viewing ? (
        <div className="mb-4 space-y-2">
          <PickedChip
            title={`${viewing.unit?.label ?? 'Unit'}${viewing.property ? ` · ${viewing.property.name}` : ''}`}
            subtitle={placeLine(viewing.property) || undefined}
            icon={<Building2 size={15} />}
          />
          <PickedChip
            title={viewing.customerName || viewing.customerPhone || 'Customer'}
            subtitle={viewing.customerName ? viewing.customerPhone : undefined}
            icon={<User size={15} />}
          />
        </div>
      ) : (
        <>
          <FormField label="Unit" required>
            <UnitPicker value={unit} onChange={setUnit} accept={viewable} fallbackCurrency={companyCurrency} autoFocus={!presetUnit} />
          </FormField>
          <FormField label="Who’s viewing" required>
            {whoOptions.length > 1 && (
              <Segmented value={who} onChange={(w) => { setWho(w); setConflict(null); }} options={whoOptions} size="sm" className="mb-2 w-full" />
            )}
            {who === 'lead' && <LeadPicker value={lead} onChange={setLead} />}
            {who === 'contact' && <ContactPicker value={contact} onChange={setContact} />}
            {who === 'other' && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" maxLength={120} aria-label="Customer name" />
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" maxLength={40} inputMode="tel" aria-label="Customer phone" />
              </div>
            )}
          </FormField>
          {matches.length > 0 && lead && (
            <div className="-mt-2 mb-4 rounded-xl border border-border bg-surface-alt/60 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">
                <Sparkles size={12} className="text-primary" /> Fits what {lead.name} asked for
              </p>
              <div className="space-y-1.5">
                {matches.map((m) => (
                  <button
                    key={m.unitUid}
                    type="button"
                    onClick={() => setUnit(m)}
                    className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-left transition-colors hover:border-primary/50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium text-text-primary">{m.label}{m.property ? ` · ${m.property.name}` : ''}</span>
                      <span className="block truncate text-2xs text-text-muted">{m.match.reasons.join(' · ') || unitSpecs(m)}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">{m.match.score}% match</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <FormField label="Agent">
        <Select value={agent} onChange={(e) => { setAgent(e.target.value); setAgentTouched(true); setConflict(null); }}>
          <option value="">Unassigned</option>
          {team.map((u) => (
            <option key={u.userUid} value={u.userUid}>{personName(u)}{u.userUid === user?.userUid ? ' (you)' : ''}</option>
          ))}
          {viewing?.agentUserUid && !team.some((u) => u.userUid === viewing.agentUserUid) && (
            <option value={viewing.agentUserUid}>{viewing.agentName || 'Current agent'}</option>
          )}
        </Select>
      </FormField>

      <div className="grid grid-cols-2 gap-x-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <FormField label="Date" required>
          <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setConflict(null); }} />
        </FormField>
        <FormField label="Time" required>
          <Input type="time" step={300} value={time} onChange={(e) => { setTime(e.target.value); setConflict(null); }} />
        </FormField>
        <div className="col-span-2 sm:col-span-1">
          <FormField label="Length">
            <Select value={String(duration)} onChange={(e) => { setDuration(Number(e.target.value)); setConflict(null); }} className="h-10">
              {durations.map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} h`}</option>)}
            </Select>
          </FormField>
        </div>
      </div>
      <p className="-mt-2 mb-4 text-2xs text-text-muted">
        Times are in {tzLabel}{browserTimezone() !== tz ? ' (your company’s timezone)' : ''}.
        {inPast && <span className="font-medium text-warning"> That time has already passed.</span>}
      </p>

      <FormField label="Notes">
        <Textarea rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Access code, parking, what they want to see…" />
      </FormField>

      {conflict && (
        <div role="alert" className="mb-3 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
          <TriangleAlert size={15} className="mt-0.5 shrink-0" />
          <span>{conflict}</span>
        </div>
      )}

      <div className="mt-2 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => void submit()} loading={busy}>{editing ? 'Save' : 'Schedule'}</Button>
      </div>
    </Modal>
  );
}
